import { ProtocolEditor } from '@/components/admin/protocoles/protocol-editor';
import { protocolViewOf, readProtocolEditorSearch, viewSearch } from '@/lib/protocol-search';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';

/**
 * Le protocole neuf - l'éditeur vide (refonte modulaire §7.1; maquette
 * `editeur-protocole.html`, écran 2).
 *
 * ## It calls nothing of its OWN
 *
 * « Pas de boîte. La carte en pointillé "Créer un nouveau protocole" ouvre
 * l'éditeur vide, nom et description en tête, une étape 1 vide dans la frise »
 * (§7.1). Before that first save there is neither entity nor server draft, so
 * what is typed lives only in the page - which is also why this is a route of
 * its own rather than `/admin/protocoles/$protocolId` with a sentinel id: an
 * editor that had to answer « is this id real » on every write would carry the
 * branch in nine places instead of in one `detail === null`.
 *
 * The editor DOES read the criteria library, and that is not a contradiction of
 * the paragraph above: nothing of the protocol exists yet, but the step editor
 * offers criteria to cite from the first keystroke, and a chooser with nothing
 * in it would be a screen that looks broken. The read is a query like any other
 * and blocks no draft (see `protocol-editor.tsx`).
 *
 * The static segment wins over the dynamic one in TanStack Router, so
 * `/admin/protocoles/nouveau` can never be read as a protocol called
 * « nouveau ».
 *
 * `onView` and `onCloseView` are no-ops here, and that is honest rather than
 * lazy: there is no history to open a version from, so the gesture cannot be
 * reached - the drawer's history renders its « Aucune version » sentence
 * instead of a single line.
 *
 * ## The switch of #154, and the generic reading already in its field
 *
 * The empty editor opens on « Conduite de l'entretien » like any other, its
 * generic reading already in the field (noyau.md §4.9, N34 - the draft is born
 * with `ANALYST_READING_TEMPLATE`, `emptyProtocolDraft`). The switch travels in
 * the URL here too (`?vue=lecture`, N47), and the first save carries it to the
 * protocol's own address; `?version=` and `?copie` mean nothing on an entity
 * that does not exist, and are ignored.
 */

export const Route = createFileRoute('/admin/protocoles/nouveau')({
  validateSearch: readProtocolEditorSearch,
  component: NouveauProtocolePage,
});

function NouveauProtocolePage() {
  const view = protocolViewOf(Route.useSearch());
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      <p className="text-gray-600 text-sm">
        <Link to="/admin/protocoles" className="text-bleu-nuit underline underline-offset-2">
          Protocoles
        </Link>
        <span className="mx-2">›</span>
        Nouveau protocole
      </p>
      <ProtocolEditor
        detail={null}
        viewing={null}
        onView={() => undefined}
        onCloseView={() => undefined}
        onReload={() => undefined}
        view={view}
        onSelectView={(next) =>
          void navigate({
            to: '/admin/protocoles/nouveau',
            search: viewSearch(next),
            replace: true,
            resetScroll: false,
          })
        }
      />
    </div>
  );
}
